import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Chip,
  Typography,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Quill modules and formats configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'align': [] }],
    ['link', 'image'],
    [{ 'color': [] }, { 'background': [] }],
    ['clean']
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent',
  'link', 'image',
  'align', 'color', 'background',
];

function PageDialog({ open, onClose, onSave, page, categories, selectedCategory }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState([]);
  const [status, setStatus] = useState('draft');
  const [currentTag, setCurrentTag] = useState('');
  const [titleError, setTitleError] = useState('');
  const [categoryError, setCategoryError] = useState('');
  
  // Reset form when dialog opens or page changes
  useEffect(() => {
    if (open) {
      if (page) {
        setTitle(page.title || '');
        setContent(page.content || '');
        setCategory(page.category?._id || page.category || '');
        setTags(page.tags || []);
        setStatus(page.status || 'draft');
      } else {
        setTitle('');
        setContent('');
        setCategory(selectedCategory?._id || '');
        setTags([]);
        setStatus('draft');
      }
      setCurrentTag('');
      setTitleError('');
      setCategoryError('');
    }
  }, [open, page, selectedCategory]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    let isValid = true;
    
    if (!title.trim()) {
      setTitleError('Page title is required');
      isValid = false;
    }
    
    if (!category) {
      setCategoryError('Category is required');
      isValid = false;
    }
    
    if (!isValid) return;
    
    onSave({
      title,
      content,
      category,
      tags,
      status
    });
  };
  
  const handleAddTag = () => {
    if (currentTag.trim() && !tags.includes(currentTag.trim())) {
      setTags([...tags, currentTag.trim()]);
      setCurrentTag('');
    }
  };
  
  const handleDeleteTag = (tagToDelete) => {
    setTags(tags.filter(tag => tag !== tagToDelete));
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {page ? 'Edit Page' : 'Create New Page'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Page Title"
            fullWidth
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setTitleError('');
            }}
            error={!!titleError}
            helperText={titleError}
            required
          />
          
          <FormControl fullWidth margin="dense" error={!!categoryError}>
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setCategoryError('');
              }}
              label="Category"
              required
            >
              {categories.map(cat => (
                <MenuItem key={cat._id} value={cat._id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
            {categoryError && (
              <Typography variant="caption" color="error">
                {categoryError}
              </Typography>
            )}
          </FormControl>
          
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Content
            </Typography>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
              <ReactQuill
                value={content}
                onChange={setContent}
                modules={quillModules}
                formats={quillFormats}
                style={{ height: '300px' }}
              />
            </Box>
          </Box>
          
          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle2" gutterBottom>
              Tags
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TextField
                size="small"
                label="Add Tag"
                value={currentTag}
                onChange={(e) => setCurrentTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                sx={{ mr: 1 }}
              />
              <Button 
                variant="outlined" 
                size="small" 
                onClick={handleAddTag}
                disabled={!currentTag.trim()}
              >
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {tags.map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleDeleteTag(tag)}
                  size="small"
                />
              ))}
            </Box>
          </Box>
          
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={status === 'published'}
                  onChange={(e) => setStatus(e.target.checked ? 'published' : 'draft')}
                  color="primary"
                />
              }
              label={status === 'published' ? 'Published' : 'Draft'}
            />
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            {page ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default PageDialog; 